import React, { useEffect, useState } from 'react';
import { Card, Switch, InputNumber, Select, Typography, Space, message, Spin, Alert, Row, Col } from 'antd';
import { MailOutlined, ClockCircleOutlined, BellOutlined, CalendarOutlined } from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const { Title, Text, Paragraph } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

export default function PassportReminderSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({ notifyEnabled: false, reminderEnabled: false, windowDays: 14, weeklyEnabled: false, weeklyDay: 1 });
  const WEEKDAYS = [
    { value: 1, label: '周一' }, { value: 2, label: '周二' }, { value: 3, label: '周三' },
    { value: 4, label: '周四' }, { value: 5, label: '周五' }, { value: 6, label: '周六' }, { value: 7, label: '周日' },
  ];

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admin/reminder-settings`, { headers: authService.getAuthHeaders() });
      setCfg(res.data);
    } catch (e) {
      message.error('加载设置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch, okMsg) => {
    try {
      setSaving(true);
      const res = await axios.put(`${API_BASE}/admin/reminder-settings`, patch, { headers: authService.getAuthHeaders() });
      setCfg(res.data);
      message.success(okMsg || '已保存');
    } catch (e) {
      message.error(e.response?.data?.error || '保存失败');
      load(); // 回滚到服务器值
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        <BellOutlined style={{ marginRight: 8 }} />护照提醒设置
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        控制系统给销售发护照上传催传邮件的规则。邮件均发给游客对应的<strong>销售</strong>（销售再转给客户上传）。
      </Paragraph>

      <Row gutter={[16, 16]}>
        {/* 初始提醒 */}
        <Col xs={24} lg={8}>
          <Card size="small" title={<span><MailOutlined style={{ marginRight: 6 }} />初始提醒</span>} style={{ height: '100%' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Row align="middle" justify="space-between">
                <Col><Text strong>订单同步时发一次</Text></Col>
                <Col>
                  <Switch
                    checked={cfg.notifyEnabled}
                    loading={saving}
                    onChange={(v) => save({ notifyEnabled: v }, v ? '已开启初始提醒' : '已关闭初始提醒')}
                    checkedChildren="开" unCheckedChildren="关"
                  />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 12 }}>
                每次自动同步拉到<strong>新订单</strong>、新建游客时，立即给销售发一次护照上传链接。已有游客不重发。
              </Text>
            </Space>
          </Card>
        </Col>

        {/* 每周提醒（出团前更早） */}
        <Col xs={24} lg={8}>
          <Card size="small" title={<span><CalendarOutlined style={{ marginRight: 6 }} />每周提醒（出团前更早）</span>} style={{ height: '100%' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Row align="middle" justify="space-between">
                <Col><Text strong>每周固定催一次</Text></Col>
                <Col>
                  <Switch
                    checked={cfg.weeklyEnabled}
                    loading={saving}
                    onChange={(v) => save({ weeklyEnabled: v }, v ? '已开启每周提醒' : '已关闭每周提醒')}
                    checkedChildren="开" unCheckedChildren="关"
                  />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col><Text>每周提醒日</Text></Col>
                <Col>
                  <Select
                    value={cfg.weeklyDay} disabled={!cfg.weeklyEnabled || saving}
                    options={WEEKDAYS}
                    onChange={(v) => save({ weeklyDay: v }, `每周提醒已设为每${WEEKDAYS.find((d) => d.value === v)?.label}`)}
                    style={{ width: 110 }}
                  />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 12 }}>
                出团日期还在<strong>提醒窗口以前</strong>（即超过下方“出团前 N 天”）的出境游团，每周在指定星期 9:00 给销售催一次，传了自动停。临近窗口后自动转为每日提醒。
              </Text>
            </Space>
          </Card>
        </Col>

        {/* 每日提醒 */}
        <Col xs={24} lg={8}>
          <Card size="small" title={<span><ClockCircleOutlined style={{ marginRight: 6 }} />每日提醒（临近出团）</span>} style={{ height: '100%' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Row align="middle" justify="space-between">
                <Col><Text strong>每天 9:00 持续催传</Text></Col>
                <Col>
                  <Switch
                    checked={cfg.reminderEnabled}
                    loading={saving}
                    onChange={(v) => save({ reminderEnabled: v }, v ? '已开启每日提醒' : '已关闭每日提醒')}
                    checkedChildren="开" unCheckedChildren="关"
                  />
                </Col>
              </Row>
              <Row align="middle" justify="space-between">
                <Col><Text>出团前提醒窗口</Text></Col>
                <Col>
                  <InputNumber
                    min={1} max={60} value={cfg.windowDays} disabled={!cfg.reminderEnabled || saving}
                    onChange={(v) => v && save({ windowDays: v }, `提醒窗口已设为出团前 ${v} 天`)}
                    addonAfter="天"
                    style={{ width: 110 }}
                  />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 12 }}>
                出团日期前 N 天起，每天给销售发提醒催仍未上传护照的游客，传了自动停。<strong>只针对出境游</strong>（入境游/一日游不催）。此窗口同时决定每周提醒的分界线。
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>

      {!cfg.notifyEnabled && !cfg.reminderEnabled && !cfg.weeklyEnabled && (
        <Alert style={{ marginTop: 16 }} type="info" showIcon
          message="当前三种提醒都已关闭，系统不会自动发送任何护照催传邮件。" />
      )}
    </div>
  );
}
